// The live-preview panel embedded on the survey's own page, beside its two
// forms. Ties the other pieces together: builds a draft from the survey's
// live form state (draft.ts, mirroring what the server assembles for
// GET /s/{token}/config) and the selected template's design, resolves the
// template's artwork references to URLs via features/assets/resolve-artwork-urls
// when an asset client context exists (null otherwise, same as everywhere
// else artwork is optional), and wires a kiosk/inline mode toggle to the
// iframe (preview-frame.tsx).
//
// The input is a DRAFT, not a saved survey: the page feeds this the live
// watched values from both the settings form and the steps form, so the
// iframe follows every keystroke — the prompt, the template swap, the mode
// toggle — without anything reaching a live placement first. Asset ids
// resolve through the same manifest cache a save resolves them through —
// the manifest doesn't care that this resolution is for a preview instead of
// a PATCH body, and a failed resolution here falls back to the template's
// last-saved URL (see draft.ts) rather than dropping the artwork.

import { Button, Card, SegmentedControl } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { Play } from "lucide-react";
import { useEffect, useState } from "react";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { useArtworkUrls } from "@/features/assets/asset-queries";
import { resolveArtworkUrls } from "@/features/assets/resolve-artwork-urls";
import { buildDraft, type DraftStep, type DraftTemplate, optionAssetIds } from "./draft";
import { PreviewFrame, type PreviewStart } from "./preview-frame";

export type PreviewTarget = number | "thanks";

/** What the preview should follow, from the page hosting this panel (a step
 *  position, or the closing screen). `seq` increments on every nav click /
 *  Edit, so re-selecting the same section resumes following even though
 *  `target` itself didn't change — see the `following` state below. */
export interface PreviewFollow {
    target: PreviewTarget;
    seq: number;
}

export interface PreviewPanelProps {
    surveyName: string;
    thankYouText: string | null;
    /** The survey's header; absent on the template page's sample. */
    displayTitle?: string | null;
    displayDescription?: string | null;
    language: string;
    steps: DraftStep[];
    /** `null` while the survey has no template selected — the draft then
     *  previews the product defaults instead of erroring. */
    template: DraftTemplate | null;
    /** `null`/absent (the template editor's own preview, which has no page
     *  sections to follow) leaves the preview on whatever it's already
     *  showing — same as before this prop existed. */
    follow?: PreviewFollow | null;
    /** False on a template page: sample content has no flow worth playing. */
    playable?: boolean;
}

export function PreviewPanel({
    surveyName,
    thankYouText,
    displayTitle = null,
    displayDescription = null,
    language,
    steps,
    template,
    follow,
    playable = true,
}: PreviewPanelProps) {
    const [mode, setMode] = useState<"kiosk" | "inline">("kiosk");
    const assetCtx = useAssetClientContext();
    const queryClient = useQueryClient();
    const [liveAssetUrls, setLiveAssetUrls] = useState<{ backgroundImageUrl?: string; logoUrl?: string }>({});

    // Following is on by default and turns off only for "Play from start",
    // which needs the preview to keep running the full flow rather than
    // snapping back to the target the moment the widget passes it. Any new
    // `seq` (a fresh nav click or Edit) means the page wants the preview
    // pointed somewhere again, so it resumes following.
    const [following, setFollowing] = useState(true);
    const [sentiment, setSentiment] = useState<"negative" | "positive">("negative");
    const [restartKey, setRestartKey] = useState(0);
    const seq = follow?.seq;
    useEffect(() => {
        if (seq !== undefined) setFollowing(true);
    }, [seq]);

    const target = following ? follow?.target : undefined;
    const targetStep = typeof target === "number" ? steps[Math.min(target, steps.length - 1)] : undefined;
    const targetIsSplit = targetStep?.kind === "multi" && (targetStep.config as { mode?: string }).mode === "split";
    const start: PreviewStart =
        target === undefined ? {} : { startAt: target, ...(targetIsSplit ? { sentiment } : {}) };

    const backgroundImageAssetId = template?.backgroundImageAssetId ?? null;
    const logoAssetId = template?.logoAssetId ?? null;

    // Option images resolve declaratively, one cached manifest per asset, so
    // an image picked on an option shows here ahead of the save that will
    // write its URL. An unresolved id keeps the option's stored URL (draft.ts).
    const optionAssetUrls = useArtworkUrls(assetCtx, optionAssetIds(steps));

    // Re-resolves whenever the selected template's artwork references
    // change (including a template swap, which changes both at once).
    // `changed: true` always — unlike a save, a preview has no dirty-fields
    // bookkeeping to consult, and always resolving is exactly "show me
    // what's there right now".
    useEffect(() => {
        let cancelled = false;
        resolveArtworkUrls(assetCtx, queryClient, {
            backgroundImage: { assetId: backgroundImageAssetId, changed: true },
            logo: { assetId: logoAssetId, changed: true },
        }).then((urls) => {
            if (cancelled) return;
            setLiveAssetUrls({
                backgroundImageUrl: urls.backgroundImageUrl ?? undefined,
                logoUrl: urls.logoUrl ?? undefined,
            });
        });
        return () => {
            cancelled = true;
        };
    }, [assetCtx, queryClient, backgroundImageAssetId, logoAssetId]);

    const config = buildDraft({
        surveyName,
        thankYouText,
        displayTitle,
        displayDescription,
        language,
        steps,
        template,
        mode,
        // Font has no live re-resolution path (resolveArtworkUrls only
        // covers background/logo, the two fields a save writes back) — the
        // template's own stored fontFaceUrl is what draft.ts falls back to.
        assetUrls: liveAssetUrls,
        optionAssetUrls,
    });

    return (
        <Card className="p-6">
            {/* Stacked: the panel's column can be 320px wide, too narrow for the
                title beside the controls. */}
            <div className="flex flex-col gap-3">
                <div>
                    <h2 className="text-base font-semibold">Live preview</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {playable
                            ? "Follows what you're editing. Play from start runs the whole flow."
                            : "Sample content in this template's look."}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {playable && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setFollowing(false);
                                setRestartKey((key) => key + 1);
                            }}
                        >
                            <Play aria-hidden="true" />
                            Play from start
                        </Button>
                    )}
                    {targetIsSplit && (
                        <SegmentedControl<"negative" | "positive">
                            aria-label="Score for the split step"
                            value={sentiment}
                            onValueChange={setSentiment}
                            options={[
                                { value: "negative", label: "Low score" },
                                { value: "positive", label: "High score" },
                            ]}
                        />
                    )}
                    <SegmentedControl<"kiosk" | "inline">
                        aria-label="Preview placement mode"
                        value={mode}
                        onValueChange={setMode}
                        options={[
                            { value: "kiosk", label: "Kiosk" },
                            { value: "inline", label: "Inline" },
                        ]}
                    />
                </div>
            </div>
            <div className="mt-4">
                <PreviewFrame config={config} start={start} restartKey={restartKey} />
            </div>
        </Card>
    );
}
